/**
 * HybridSeriesProvider — "Both": historical context from Doris plus the live
 * NATS tail, composed from the other two providers. History is loaded for the
 * window (or a recent default in live mode) and NATS keeps appending live
 * points on top.
 */

import { DorisSeriesProvider } from './DorisSeriesProvider';
import { WorkerNatsSeriesProvider } from './WorkerNatsSeriesProvider';
import type {
  ProviderContext,
  SeriesProvider,
  SeriesPatch,
  SeriesPatchHandler,
  SeriesSubscription,
} from './SeriesProvider';
import type { ResolvedRange } from '../spec';

/** Default history window when hybrid is opened in live mode (3 h). */
const DEFAULT_HYBRID_HISTORY_MS = 3 * 60 * 60 * 1000;

export class HybridSeriesProvider implements SeriesProvider {
  readonly mode = 'hybrid' as const;

  private doris = new DorisSeriesProvider();
  private nats = new WorkerNatsSeriesProvider();

  open(ctx: ProviderContext, range: ResolvedRange, onPatch: SeriesPatchHandler): SeriesSubscription {
    const subs: SeriesSubscription[] = [];

    // Historical context from Doris (reset patch).
    const historyRange: ResolvedRange =
      range.kind === 'window'
        ? range
        : { kind: 'window', startMs: Date.now() - DEFAULT_HYBRID_HISTORY_MS, endMs: Date.now() };
    subs.push(this.doris.open(ctx, historyRange, onPatch));

    // Live tail from NATS (incremental patches, appended after history).
    subs.push(this.nats.open(ctx, { kind: 'live' }, onPatch));

    return {
      close() {
        subs.forEach((s) => s.close());
      },
    };
  }

  loadDetail(ctx: ProviderContext, startMs: number, endMs: number): Promise<SeriesPatch> {
    return this.doris.loadDetail(ctx, startMs, endMs);
  }
}
